import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ExpensesService } from '../../features/expenses/services/expenses.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { AddExpenseDialogComponent } from './add-expense-dialog.component';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FileExportService } from '../../shared/services/file-export.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { PermissionService } from '../../shared/services/permission.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';

@Component({
  selector: 'app-expenses',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    AgGridAngular,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  templateUrl: './expenses.component.html',
  styleUrl: './expenses.component.scss',
})
export class ExpensesListComponent implements OnInit {
  public viewPortSize: boolean = false;
  loading: boolean = false;
  error: any = '';
  rowData = [];
  readonly dialog = inject(MatDialog);
  isDialogOpen = false; // Flag to prevent multiple dialog openings
  columnDefs: any[];
  search: any;
  advancedSearch: any;
  private searchSubject = new Subject<string>();
  page = 0;
  pageSize = 10;
  totalPages = 1;
  selectedBranchId: any;
  pageSizeOptions = [5, 10, 20, 50, 100];
  isExportOpen = false;
  exportFormat: string = 'pdf';
  exportLoading: boolean = false;
  exportForm: FormGroup;
  fieldOptions = [
    { key: 'expenseType', label: 'Expense Type' },
    { key: 'amount', label: 'Amount' },
    { key: 'expenseDate', label: 'Expense Date' },
    { key: 'paidByData.firstName', label: 'Paid By' },
    { key: 'paymentMethod', label: 'Payment Method' },
    { key: 'expenseStatus', label: 'Status' },
  ];

  // Date range properties
  startDate: any;
  endDate: any;
  dateRangeForm: FormGroup;
  canListExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'List'
  );
  canCreateExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'Create'
  );
canEditExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'Edit'
  );
  canDeleteExpenses$ = this.permissionService.hasPermission(
    ModuleIds.Expenses,
    'Delete'
  );
  canEditExpenses: boolean = false;

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Remove time component
    return date ? date <= today : false;
  };

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private expensesService: ExpensesService,
    private fileExportService: FileExportService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private permissionService: PermissionService
  ) {
    this.exportForm = this.fb.group({
      fields: this.fb.array([]),
    });
  }

  /**
   * Convert timestamp to DD/MM/YYYY format
   * @param timestamp - Unix timestamp in milliseconds
   * @returns Formatted date string
   */
  private formatDate(timestamp: string | number): string {
    if (!timestamp) return '--';
    const date = new Date(parseInt(timestamp.toString()));
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  ngOnInit() {
    const selectedBranchId: any = localStorage.getItem('selectedBranchId');
    const branch = JSON.parse(selectedBranchId);
    this.selectedBranchId = branch;

    // Initialize date range form
    this.dateRangeForm = this.fb.group({
      startDate: [''],
      endDate: [''],
    });

    this.fetchAllExpenses();

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(
          (searchTerm: any) =>
            searchTerm.trim().length >= 3 || searchTerm.trim().length === 0
        )
      )
      .subscribe((searchTerm) => {
        this.search = searchTerm.trim();
        this.fetchAllExpenses();
      });

    this.checkViewportSize();
    this.canEditExpenses$.subscribe(val => {
      this.canEditExpenses = val;
    });
  }

  // Date range change handler
  onDateChange(event: any, type: string) {
    const formattedDate = this.datePipe.transform(event.value, 'yyyy-MM-dd');
    if (type === 'start') {
      this.startDate = formattedDate;
    } else if (type === 'end') {
      this.endDate = formattedDate;
    }
    if (this.startDate != null && this.endDate != null) {
      this.fetchAllExpenses();
    }
  }

  // Clear filters
  onClear() {
    this.dateRangeForm.reset();
    this.startDate = null;
    this.endDate = null;
    this.search = '';
    this.advancedSearch = null;
    this.page = 0;
    this.fetchAllExpenses();
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  isDropdownOpen = false;

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        headerName: 'Expense Type',
        field: 'expenseType',
        flex: this.viewPortSize ? 1 : 0,
        filter: true,
      },
      {
        headerName: 'Amount',
        field: 'amount',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          return params.value ? `AED ${params.value.toFixed(2)}` : '--';
        }
      },
      {
        headerName: 'Expense Date',
        field: 'expenseDate',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          return this.formatDate(params.value);
        }
      },
      {
        headerName: 'Paid By',
        field: 'paidByName',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Payment Method',
        field: 'paymentMethod',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Status',
        field: 'expenseStatus',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const status = params.value;
          let color = '#666';
          if (status === 'Approved') color = '#00a860';
          else if (status === 'Pending') color = '#efbe12';
          else if (status === 'Rejected') color = '#ab0010';
          
          return `<span style="color: ${color}; font-weight: 500;">${status || '--'}</span>`;
        }
      },
      {
        field: 'action',
        headerName: 'Actions',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.gap = '20px';

          const createButton = (iconSrc: string, onClick: () => void) => {
            const span = document.createElement('span');
            span.style.cursor = 'pointer';
            span.style.display = 'flex';
            span.innerHTML = `<img src="${iconSrc}"/>`;
            span.addEventListener('click', (event) => {
              event.stopPropagation(); // Prevent event bubbling to cell click
              event.preventDefault();
              onClick();
            });
            return span;
          };
          if(this.canEditExpenses){
          container.appendChild(createButton('assets/images/icons/icon-edit.svg', () => this.onEdit(params?.data?.id)));
          }
          return container;
        },
        floatingFilter: false,
        filter: false,
      },
    ];
  }

  public defaultColDef: ColDef = {
    filter: true,
    floatingFilter: true,
    resizable: true,
    sortable: true
  };
  
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public themeClass: string = 'ag-theme-quartz';
  gridApi: any;

  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  toggleExport(event: MouseEvent) {
    event.stopPropagation();
    this.isExportOpen = !this.isExportOpen;
  }

  onFilterChanged() {
    const filterModel = this.gridApi.getFilterModel();
  
    this.advancedSearch = {};
  
    // Loop dynamically through all filters
    Object.keys(filterModel).forEach((colKey) => {
      const filterValue = filterModel[colKey]?.filter;
      if (filterValue !== undefined && filterValue !== null) {
        this.advancedSearch[colKey] = String(filterValue); // 👈 force string
      }
    });
  
    this.page = 0; // reset pagination if needed
    this.fetchAllExpenses(this.pageSize);
  }
  
  
  private fetchAllExpenses(pageSize: number = 10) {    
    if (pageSize) {
      this.pageSize = pageSize;
    }
    const skip = this.page * this.pageSize;
    const input = {
      search: this.search,
      limit: this.pageSize,
      advancedSearch: this.advancedSearch,
      skip,
      startDate: this.startDate,
      endDate: this.endDate,
    }
    this.expensesService
      .findAllExpensesWithPagination(input)
      .subscribe({
        next: (expenses: any) => {
          // Calculate total pages based on the total count from API
          const total = expenses?.total || 0;
          this.totalPages = Math.ceil(total / this.pageSize);
          
          this.rowData = (expenses?.expenses || []).map((expense: any) => {
            return {
              id: expense?.id ?? '--',
              expenseType: expense?.expenseType ?? '--',
              amount: expense?.amount ?? 0,
              expenseDate: expense?.expenseDate ?? '--',
              paidByName: expense?.paidByData?.firstName ?? '--',
              paymentMethod: expense?.paymentMethod ?? '--',
              expenseStatus: expense?.expenseStatus ?? '--',
              tax: expense?.tax ?? 0,
            };
          });
        },
        error: (error: any) => {
          this.error = error;
          this.loading = false;
        },
      });
  }

  get exportFieldsArray() {
    return this.exportForm.get('fields') as FormArray;
  }

  onFieldCheckboxChange(event: any, fieldName: string) {
    const fieldsArray: FormArray = this.exportForm.get('fields') as FormArray;

    if (event.target.checked) {
      fieldsArray.push(new FormControl(fieldName));
    } else {
      const index = fieldsArray.controls.findIndex(
        (control) => control.value === fieldName
      );
      if (index >= 0) {
        fieldsArray.removeAt(index);
      }
    }
  }

  exportExpenses() {
    const selectedFields = this.exportForm.value.fields;

    if (selectedFields.length === 0) {
      this.toastr.error('Please select at least one field to export.');
      return;
    }

    let exportObservable;
    const input = {
      search: this.search,
    }
    switch (this.exportFormat) {
      case 'pdf':
        exportObservable =
          this.expensesService.exportExpensesToPDF(input, selectedFields);
        break;
      case 'csv':
        exportObservable =
          this.expensesService.exportExpensesToCSV(input, selectedFields);
        break;
      default:
        this.toastr.error('Invalid export format selected.');
        return;
    }

    this.exportLoading = true;
    exportObservable.subscribe({
      next: (response: Blob) => {
        this.exportLoading = false;
        this.getFile(response, this.exportFormat);
      },
      error: (error) => {
        this.exportLoading = false;
        this.toastr.error(
          'Failed to export expenses. Please try again.'
        );
      },
    });
  }

  async getFile(response: any, format: string) {
    try {
      const file = await this.fileExportService.getFileUrlPost(
        response,
        format
      );
    } catch (error) {
      console.error('Error exporting file', error);
    }
  }

  // onFilterChanged() {
  //   const filterModel = this.gridApi.getFilterModel();
  
  //   const advancedSearch: any = {
  //     //expenseType: filterModel.expenseType?.filter || '',
  //     amount: filterModel.amount?.filter || '',
  //     paidByName: filterModel.paidByName?.filter || '',
  //     //paymentMethod: filterModel.paymentMethod?.filter || '',
  //     //expenseStatus: filterModel.expenseStatus?.filter || '',
  //   };
  
  //   this.advancedSearch = advancedSearch;
  //   this.page = 0;
  //   this.fetchAllExpenses();
  // }

  nextPage() {
    if (this.page + 1 < this.totalPages) {
      this.page++;
      this.fetchAllExpenses(this.pageSize);
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.fetchAllExpenses(this.pageSize);
    }
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    this.pageSize = newSize;
    this.page = 0;
    this.fetchAllExpenses(this.pageSize);
  }

  onCellClicked(params: any) {
    const expenseId = params.data?.id;

    if (
      params.column.colId === 'action' &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;

      if (action === 'edit') {
        this.onEdit(expenseId);
      } else if (action === 'delete') {
        this.onConfirmDelete(expenseId);
      }
    } else {
      this.router.navigate([`/expenses/details/${expenseId}`]);
    }
  }

  onConfirmDelete(id: number): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Delete',
        message: 'Are you sure you want to delete this expense?',
        confirmText: 'Delete'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.onDelete(id);
      }
    });
  }

  onDelete(id: any) {
    this.expensesService.removeExpense(id).subscribe({
      next: (result) => {
        if (result) {
          this.toastr.success('Expense deleted successfully', 'Success');
          this.fetchAllExpenses();
        }
      },
      error: (error) => {
        this.toastr.error(error, 'Error');
      },
    });
  }

  onEdit(id: any) {
    // Always fetch fresh expense data from API for editing
    console.log('=== EDIT BUTTON CLICKED ===');
    console.log('Timestamp:', new Date().toISOString());
    console.log('Fetching expense with ID:', id);
    console.log('Stack trace:', new Error().stack);
    
    this.expensesService.getExpenseById(id).subscribe({
      next: (expenseData) => {
        console.log('=== API RESPONSE ===');
        console.log('Raw API response:', expenseData);
        console.log('Response type:', typeof expenseData);
        console.log('Response keys:', expenseData ? Object.keys(expenseData) : 'null');
        console.log('==================');
        
        if (expenseData && expenseData.id) {
          // Pass the fresh API data to dialog
          this.openEditDialog(expenseData);
        } else {
          console.error('Invalid expense data received:', expenseData);
          this.toastr.error('Expense not found or invalid data', 'Error');
        }
      },
      error: (error) => {
        console.error('=== API ERROR ===');
        console.error('Error details:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        console.error('================');
        this.toastr.error('Failed to fetch expense data', 'Error');
      }
    });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.pageSize = 10;
    this.page = 0;
    this.searchSubject.next(input.value);
  }

  openDialog() {
    if (this.isDialogOpen) {
      console.log('Dialog is already open, preventing duplicate opening');
      return;
    }
    
    this.isDialogOpen = true;
    const dialogRef = this.dialog.open(AddExpenseDialogComponent, { 
      disableClose: true,
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      autoFocus: false,
      panelClass: 'expense-dialog'
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.isDialogOpen = false; // Reset the flag when dialog closes
      if (result && result.success) {
        console.log('Expense created successfully:', result.data);
        this.fetchAllExpenses(); // Refresh the expense list
      }
    });
  }

  openEditDialog(expenseData: any) {
    console.log('=== OPENING EDIT DIALOG ===');
    console.log('Timestamp:', new Date().toISOString());
    console.log('Expense data:', expenseData);
    console.log('Is dialog already open?', this.isDialogOpen);
    
    if (this.isDialogOpen) {
      console.log('Dialog is already open, preventing duplicate opening');
      return;
    }
    
    this.isDialogOpen = true;
    const dialogRef = this.dialog.open(AddExpenseDialogComponent, {
      disableClose: true,
      data: expenseData,
      width: '90vw',
      maxWidth: '1200px',
      height: '90vh',
      autoFocus: false,
      panelClass: 'expense-dialog'
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log('Edit dialog closed with result:', result);
      this.isDialogOpen = false; // Reset the flag when dialog closes
      console.log('Dialog flag reset to false');
      
      if (result && result.success) {
        console.log('Expense updated successfully:', result.data);
        console.log('Refreshing expense list...');
        this.fetchAllExpenses(); // Refresh the expense list
      } else {
        console.log('Dialog closed without success or result is null');
      }
    });
  }
} 